import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const PositionFormModal = ({ position, board, parts, planId, isOpen, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    partId: '',
    instanceId: 1,
    positionX: 0,
    positionY: 0,
    rotation: 'A',
    cutSequence: null,
    cutStatus: 'PENDING'
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (position) {
      setFormData({
        partId: position.part_id || '',
        instanceId: position.instance_id || 1,
        positionX: position.position_x || 0,
        positionY: position.position_y || 0,
        rotation: position.rotation || 'A',
        cutSequence: position.cut_sequence || null,
        cutStatus: position.cut_status || 'PENDING'
      });
    } else {
      setFormData({
        partId: '',
        instanceId: 1,
        positionX: 0,
        positionY: 0,
        rotation: 'A',
        cutSequence: null,
        cutStatus: 'PENDING'
      });
    }
  }, [position, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'partId' || name === 'instanceId' || name === 'cutSequence' ? parseInt(value) || 0 :
             name === 'positionX' || name === 'positionY' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        cutPlanId: parseInt(planId),
        boardId: parseInt(board.id),
        partId: parseInt(formData.partId),
        instanceId: formData.instanceId,
        positionX: formData.positionX,
        positionY: formData.positionY,
        rotation: formData.rotation,
        cutSequence: formData.cutSequence,
        cutStatus: formData.cutStatus
      };

      await onSave(position?.id, data);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar posição:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const selectedPart = parts.find(p => p.id === parseInt(formData.partId));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {position ? 'Editar Posição' : 'Adicionar Peça na Chapa'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {!position && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selecionar Peça *
              </label>
              <select
                name="partId"
                value={formData.partId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecione uma peça...</option>
                {parts.map((part) => (
                  <option key={part.id} value={part.id}>
                    {part.part_code} - {part.part_name} ({part.width}x{part.height})
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedPart && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">{selectedPart.part_name}</p>
              <p className="text-xs text-gray-500">
                Dimensões: {selectedPart.width} x {selectedPart.height}mm
              </p>
              <p className="text-xs text-gray-500">Material: {selectedPart.material_name}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Posição X (mm) *
              </label>
              <input
                type="number"
                name="positionX"
                value={formData.positionX}
                onChange={handleChange}
                required
                min="0"
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Posição Y (mm) *
              </label>
              <input
                type="number"
                name="positionY"
                value={formData.positionY}
                onChange={handleChange}
                required
                min="0"
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rotação
              </label>
              <select
                name="rotation"
                value={formData.rotation}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="A">Automática (A)</option>
                <option value="H">Horizontal (H)</option>
                <option value="V">Vertical (V)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status do Corte
              </label>
              <select
                name="cutStatus"
                value={formData.cutStatus}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="PENDING">Pendente</option>
                <option value="IN_PROGRESS">Em Corte</option>
                <option value="COMPLETED">Cortada</option>
                <option value="CANCELLED">Cancelada</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sequência de Corte
            </label>
            <input
              type="number"
              name="cutSequence"
              value={formData.cutSequence || ''}
              onChange={handleChange}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ordem de corte (opcional)"
            />
          </div>

          <div className="flex justify-between pt-4 border-t border-gray-200">
            <div>
              {position && (
                <button
                  type="button"
                  onClick={() => onDelete?.(position)}
                  className="px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                >
                  Remover
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PositionFormModal;
